import {
  faAngleDown,
  faCheckCircle,
  faClock,
  faTimes
} from '@fortawesome/pro-light-svg-icons';
import { faBookSpells } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown, { DropdownOption } from 'components/shared/Dropdown';
import TooltipMF from 'components/shared/TooltipMF';
import { Heading2 } from 'components/shared/Typography';
import { useI18n } from 'hooks/useI18n';
import { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  StyledBadge,
  StyledFilter,
  StyledFilters,
  StyledFiltersDropdown,
  StyledHired,
  StyledMainFilters,
  StyledProcess,
  StyledProcesses,
  StyledSection,
  StyledSource,
  StyledStep,
  StyledStepConversion,
  StyledStepConversionNumber,
  StyledStepDays,
  StyledStepTitle
} from './HPCComparison.styled';
import HPCEmpty from './HPCEmpty';

type ContentProps = {
  content?: any[];
};

const HPCComparison: React.FC<ContentProps> = ({ content }) => {
  const { t } = useI18n('hiring-process-comparison');

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 300,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 680,
        settings: {
          slidesToShow: 1
        }
      }
    ]
  };

  const [filteredCompanies, setFilteredCompanies] = useState<any[]>([]);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [initialRender, setInitialRender] = useState(true);

  const companySelected = (id: number): void => {
    if (!filteredCompanies.find((e) => e.id === id)) {
      const company = content?.find((e) => e.id === id);
      setFilteredCompanies((filteredCompanies) => [
        company,
        ...filteredCompanies
      ]);
      setInitialRender(false);

      ServiceEventTracking.track({
        type: 'Website - Hiring Processes Landing Interaction',
        properties: {
          objectName: company.attributes.companyName,
          action: 'add'
        }
      });
    } else {
      setFilteredCompanies((filteredCompanies) =>
        filteredCompanies.filter((e) => {
          return e.id != id;
        })
      );
      setInitialRender(false);
    }
  };

  const removeCompany = (id: number): void => {
    setFilteredCompanies((filteredCompanies) =>
      filteredCompanies.filter((e) => {
        return e.id != id;
      })
    );
    setInitialRender(false);
  };

  const checkCompanyState = (id: number): boolean => {
    if (filteredCompanies.find((e) => e.id === id)) {
      return true;
    } else {
      return false;
    }
  };

  const companiesArray: DropdownOption[] = [];

  content
    ?.sort((a, b) =>
      a.attributes.companyName
        .toLowerCase()
        .localeCompare(b.attributes.companyName.toLowerCase())
    )
    .forEach((e) => {
      if (e.attributes.filterPosition > 0) {
        return;
      }
      const element = {
        label: e.attributes.companyName,
        value: e.attributes.companyName,
        action: () => {
          companySelected(e.id);
          setFilterMenuOpen(false);
        },
        activeClassCondition: filteredCompanies.find((z) => z.id === e.id)
      };
      companiesArray.push(element);
    });

  const node = useRef<HTMLDivElement>(null);

  const handleCloseFilterMenu = (e: MouseEvent): void => {
    if (node.current?.contains(e.target as Node)) {
      return;
    }
    setFilterMenuOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleCloseFilterMenu);

    return () => {
      document.removeEventListener('mousedown', handleCloseFilterMenu);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramsIdArray = params.get('companies')?.split(',');
    if (
      !!paramsIdArray &&
      paramsIdArray.length > 0 &&
      paramsIdArray[0] !== ''
    ) {
      const filteredContent: any[] = [];
      paramsIdArray?.forEach((e) => {
        const arrEl = content?.filter((el) => el.id === parseInt(e));
        filteredContent.push(...(arrEl || []));
      });
      setFilteredCompanies(filteredContent);
    } else {
      content
        ?.sort(
          (a, b) =>
            a.attributes.defaultColumnNumber - b.attributes.defaultColumnNumber
        )
        .forEach((e) => {
          if (e.attributes.defaultColumnNumber > 0) {
            setFilteredCompanies((filteredCompanies) => [
              ...filteredCompanies,
              e
            ]);
          }
        });
    }
  }, [content]);

  useEffect(() => {
    if (!initialRender) {
      const companiesIds: number[] = [];
      filteredCompanies.forEach((e) => {
        companiesIds.push(e.id);
      });
      window.history.replaceState(
        {},
        '',
        `?companies=${companiesIds}${window.location.hash}`
      );
    }
  }, [filteredCompanies, initialRender]);

  return (
    <StyledSection id="comparator">
      <Heading2 editorial>
        {t('hiring-process-comparison.process_comparison.title')}
      </Heading2>
      <StyledFilters>
        <StyledMainFilters>
          {content
            ?.sort(
              (a, b) =>
                a.attributes.filterPosition - b.attributes.filterPosition
            )
            .map(
              ({ id, attributes }) =>
                attributes.filterPosition > 0 && (
                  <StyledFilter
                    key={id}
                    onClick={() => companySelected(id)}
                    className={`${checkCompanyState(id) ? 'is-active' : ''}`}
                  >
                    <img src={attributes.logoMini.data.attributes.url} alt="" />
                    {attributes.companyName}
                  </StyledFilter>
                )
            )}
        </StyledMainFilters>
        <StyledFiltersDropdown ref={node}>
          <StyledFilter
            aria-controls="filter-menu"
            onClick={() => setFilterMenuOpen(!filterMenuOpen)}
          >
            {t(
              'hiring-process-comparison.process_comparison.show_more_companies'
            )}
            <FontAwesomeIcon icon={faAngleDown} />
          </StyledFilter>
          <Dropdown
            id="filter-menu"
            options={companiesArray}
            style={{ display: filterMenuOpen ? 'block' : 'none' }}
            hasFilter
            filterPlaceholder={
              t(
                'hiring-process-comparison.process_comparison.search_companies'
              ) || ''
            }
          />
        </StyledFiltersDropdown>
      </StyledFilters>
      <StyledProcesses>
        {filteredCompanies.length > 0 && (
          <Slider {...sliderSettings}>
            {filteredCompanies.map(({ id, attributes }) => (
              <StyledProcess key={id}>
                <StyledBadge>
                  <img src={attributes.logoMini.data.attributes.url} alt="" />
                  <span>{attributes.companyName}</span>
                  <button onClick={() => removeCompany(id)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </StyledBadge>
                <StyledSource>
                  <FontAwesomeIcon icon={faBookSpells} />
                  {attributes.sourceLink && (
                    <a
                      href={attributes.sourceLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('hiring-process-comparison.process_comparison.source')}
                    </a>
                  )}
                  {!attributes.sourceLink && (
                    <TooltipMF
                      content={t(
                        'hiring-process-comparison.process_comparison.source_empty'
                      )}
                      direction="up"
                    >
                      <span>
                        {t(
                          'hiring-process-comparison.process_comparison.source'
                        )}
                      </span>
                    </TooltipMF>
                  )}
                </StyledSource>
                <div>
                  {attributes.hiring_process_steps.data
                    .sort(
                      (a: any, b: any) =>
                        a.attributes.order - b.attributes.order
                    )
                    .map(({ id, attributes }: any) => (
                      <StyledStep key={id}>
                        <StyledStepTitle>{attributes.title}</StyledStepTitle>
                        {attributes.days && (
                          <StyledStepDays>
                            <FontAwesomeIcon icon={faClock} />
                            <span>
                              {t(
                                'hiring-process-comparison.process_comparison.duration'
                              )}{' '}
                              {t(
                                'hiring-process-comparison.process_comparison.duration_day',
                                {
                                  count: attributes.days
                                }
                              )}
                            </span>
                          </StyledStepDays>
                        )}
                        <p>{attributes.description}</p>
                        {attributes.conversion && (
                          <StyledStepConversion>
                            <StyledStepConversionNumber>
                              {attributes.conversion}%
                            </StyledStepConversionNumber>
                            <span>
                              {t(
                                'hiring-process-comparison.process_comparison.conversion'
                              )}
                            </span>
                          </StyledStepConversion>
                        )}
                      </StyledStep>
                    ))}
                </div>
                <StyledHired>
                  <FontAwesomeIcon icon={faCheckCircle} />

                  <p>
                    {attributes.finalStepText
                      ? attributes.finalStepText
                      : t(
                          'hiring-process-comparison.process_comparison.hired_weeks'
                        )}
                  </p>

                  <h4>
                    {t('hiring-process-comparison.process_comparison.hired')}
                  </h4>
                </StyledHired>
              </StyledProcess>
            ))}
          </Slider>
        )}
        {filteredCompanies.length === 0 && <HPCEmpty />}
      </StyledProcesses>
    </StyledSection>
  );
};

export default HPCComparison;
